import type { ChannelMessage, CreateMessagePoll, MessagePoll, ServerToClientEvents } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { toast } from "../../components/ui/Toast";
import { patchMessage } from "./cache";
import type { MessageActions } from "./types";

type PollEvent = Parameters<ServerToClientEvents["message:poll"]>[0];

/** Vote / end handlers for channel message polls, writing results into the chat cache. */
export function pollActions(key: unknown[]): NonNullable<MessageActions["poll"]> {
  const set = (id: string, poll: MessagePoll) => patchMessage<ChannelMessage>(key, id, (m) => ({ ...m, poll }));
  return {
    vote: async (m, optionIds) => {
      try {
        const r = await api.put<{ poll: MessagePoll }>(`/api/messages/${m.id}/poll/votes`, { optionIds });
        set(m.id, r.poll);
      } catch (e) {
        toast.error(errorMessage(e, "Couldn't record your vote"));
        throw e;
      }
    },
    end: async (m) => {
      try {
        const r = await api.post<{ poll: MessagePoll }>(`/api/messages/${m.id}/poll/end`);
        set(m.id, r.poll);
      } catch (e) {
        toast.error(errorMessage(e, "Couldn't end the poll"));
        throw e;
      }
    },
  };
}

/** Live poll counts: keep this viewer's own votes unless they cast this one. */
export function applyPollEvent(p: PollEvent, meId: string) {
  patchMessage<ChannelMessage>(["messages", p.channelId], p.id, (m) => ({
    ...m,
    poll: { ...p.poll, myVotes: p.voterId === meId ? p.voterVotes : (m.poll?.myVotes ?? []) },
  }));
}

/**
 * A full message update is hydrated from one viewer's point of view. Keep the
 * fields that are per-viewer (own reactions, own poll votes) from what we have.
 */
export function keepViewerState(prev: ChannelMessage, next: ChannelMessage): ChannelMessage {
  const mine = new Set((prev.reactions ?? []).filter((r) => r.me).map((r) => r.emoji));
  return {
    ...next,
    reactions: (next.reactions ?? []).map((r) => ({ ...r, me: mine.has(r.emoji) })),
    poll: next.poll && prev.poll ? { ...next.poll, myVotes: prev.poll.myVotes } : next.poll,
  };
}

/** What a poll looks like before the server confirms it (shown on the pending message). */
export function optimisticPoll(p: CreateMessagePoll): MessagePoll {
  return {
    question: p.question,
    options: p.options.map((o, i) => ({ id: `pending-${i}`, text: o.text, emoji: o.emoji ?? null, votes: 0 })),
    multiple: p.multiple,
    endsAt: new Date(Date.now() + p.durationHours * 3600_000).toISOString(),
    closed: false,
    totalVoters: 0,
    myVotes: [],
  };
}
